import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useFormik } from 'formik';
import { FormLabel, FormControl, Input, FormHelperText, Box, Stack } from '@mui/material';
import { patchApi } from 'views/services/api';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTheme } from '@mui/material';

import { employeeSchemaForEdit } from 'schema/employeeSchema';
import { Checkbox, FormControlLabel, List, ListItem } from '@mui/material';
import { Upload, Person, Phone, Badge as BadgeIcon, Home, Work, AttachMoney, Schedule } from '@mui/icons-material';

const EditEmployee = (props) => {
  const { open, handleClose, employeeData } = props;
  const theme = useTheme();

  const [currentIdFile, setCurrentIdFile] = useState(null);
  const [currentIdFile2, setCurrentIdFile2] = useState(null);
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);

  const initialValues = {
    employeeType: employeeData?.employeeType || 'default',
    shift: employeeData?.shift || 'default',
    phoneNumber: employeeData?.phoneNumber,
    firstName: employeeData?.firstName,
    lastName: employeeData?.lastName,
    idCardType: employeeData?.idCardType || 'default',
    idcardNumber: employeeData?.idcardNumber,
    address: employeeData?.address,
    salary: employeeData?.salary,
    idFile: employeeData?.idFile ? employeeData.idFile.split('/').pop() : '',
    idFile2: employeeData?.idFile2 ? employeeData.idFile2.split('/').pop() : '',
    permissions: employeeData?.permissions || []
  };

  const AddData = async (values) => {
    try {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('phoneNumber', values.phoneNumber);
      formData.append('shift', values.shift);
      formData.append('employeeType', values.employeeType);
      formData.append('idCardType', values.idCardType);
      formData.append('idcardNumber', values.idcardNumber);
      formData.append('address', values.address);
      formData.append('salary', values.salary);

      // Enviar solo si subieron algo nuevo
      if (currentIdFile) formData.append('idFile', currentIdFile);
      if (currentIdFile2) formData.append('idFile2', currentIdFile2);

      values.permissions.forEach((p) => {
        formData.append('permissions[]', p);
      });

      let response = await patchApi(`api/employee/editemployee/${employeeData?._id}`, formData);
      if (response.status === 200) {
        toast.success('Empleado modificado correctamente');
        setShow(false);
        setShow2(false);
        handleClose();
      } else {
        toast.error('Error al modificar Empleado');
      }
    } catch (e) {
      toast.error('Error al modificar Empleado');
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: employeeSchemaForEdit,
    enableReinitialize: true,
    onSubmit: async (values) => {
      AddData(values);
    }
  });

  const handleFileChange = (field, event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue(field, file.name);
      if (field === 'idFile') setCurrentIdFile(file);
      if (field === 'idFile2') setCurrentIdFile2(file);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}
      >
        <Typography variant="h5" fontWeight="600">Editar Empleado</Typography>
        <Button onClick={handleClose} sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}>
          Cancelar
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 4 }}>
        <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
          <Grid container rowSpacing={3.5} columnSpacing={{ xs: 0, sm: 3, md: 4 }}>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Work fontSize="small" color="primary" />
                  <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Cargo del Empleado</FormLabel>
                </Stack>
                <Select
                  id="employeeType"
                  name="employeeType"
                  size="medium"
                  fullWidth
                  value={formik.values.employeeType}
                  onChange={formik.handleChange}
                  error={formik.touched.employeeType && Boolean(formik.errors.employeeType)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="default" disabled>Selecciona el Cargo</MenuItem>
                  <MenuItem value="manager">Gerente</MenuItem>
                  <MenuItem value="cleaning">Personal de Limpieza</MenuItem>
                  <MenuItem value="reception">Recepcionista</MenuItem>
                  <MenuItem value="cook">Cocinero(a)</MenuItem>
                  <MenuItem value="waiter">Mesero(a)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Schedule fontSize="small" color="primary" />
                  <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Turno de Trabajo</FormLabel>
                </Stack>
                <Select
                  id="shift"
                  name="shift"
                  size="medium"
                  fullWidth
                  value={formik.values.shift}
                  onChange={formik.handleChange}
                  error={formik.touched.shift && Boolean(formik.errors.shift)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="default" disabled>Selecciona el Turno</MenuItem>
                  <MenuItem value="Day-Shift 08:00 AM - 08:00 PM">Día (08:00 AM - 08:00 PM)</MenuItem>
                  <MenuItem value="Night-Shift 08:00 PM - 08:00 AM">Noche (08:00 PM - 08:00 AM)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Person fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Nombre</FormLabel>
              </Stack>
              <TextField
                id="firstName"
                name="firstName"
                size="medium"
                fullWidth
                placeholder="Ingresar Nombre"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Person fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Apellido</FormLabel>
              </Stack>
              <TextField
                id="lastName"
                name="lastName"
                size="medium"
                fullWidth
                placeholder="Ingresar Apellido"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <BadgeIcon fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Tipo de Documento</FormLabel>
              </Stack>
              <Select
                id="idCardType"
                name="idCardType"
                size="medium"
                fullWidth
                value={formik.values.idCardType}
                onChange={formik.handleChange}
                error={formik.touched.idCardType && Boolean(formik.errors.idCardType)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="default" disabled>Seleccionar Tipo</MenuItem>
                <MenuItem value="Aadharcard">Cédula de Identidad</MenuItem>
                <MenuItem value="VoterIdCard">Pasaporte</MenuItem>
                <MenuItem value="PanCard">RIF</MenuItem>
                <MenuItem value="DrivingLicense">Licencia de Conducir</MenuItem>
              </Select>
              <FormHelperText error={formik.touched.idCardType && formik.errors.idCardType}>
                {formik.touched.idCardType && formik.errors.idCardType}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <BadgeIcon fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Número de Documento</FormLabel>
              </Stack>
              <TextField
                id="idcardNumber"
                name="idcardNumber"
                size="medium"
                type="text"
                fullWidth
                placeholder="Ingresar número"
                value={formik.values.idcardNumber}
                onChange={formik.handleChange}
                error={formik.touched.idcardNumber && Boolean(formik.errors.idcardNumber)}
                helperText={formik.touched.idcardNumber && formik.errors.idcardNumber}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 1 }}>Documento Frente</FormLabel>
              {show ? (
                <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Input id="idFile" name="idFile" type="file" disableUnderline onChange={(event) => handleFileChange('idFile', event)} accept="image/*" />
                  {formik.values.idFile && <Typography variant="caption" color="textSecondary">Archivo listado: {formik.values.idFile}</Typography>}
                </Box>
              ) : (
                <Button variant="outlined" startIcon={<Upload />} onClick={() => setShow(true)} sx={{ borderRadius: 2, py: 1.5, textTransform: 'none' }} fullWidth>
                  Cambiar Frente del Doc.
                </Button>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 1 }}>Documento Reverso</FormLabel>
              {show2 ? (
                <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Input id="idFile2" name="idFile2" type="file" disableUnderline onChange={(event) => handleFileChange('idFile2', event)} accept="image/*" />
                  {formik.values.idFile2 && <Typography variant="caption" color="textSecondary">Archivo listado: {formik.values.idFile2}</Typography>}
                </Box>
              ) : (
                <Button variant="outlined" startIcon={<Upload />} onClick={() => setShow2(true)} sx={{ borderRadius: 2, py: 1.5, textTransform: 'none' }} fullWidth>
                  Cambiar Reverso del Doc.
                </Button>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Phone fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Número de Teléfono</FormLabel>
              </Stack>
              <TextField
                id="phoneNumber"
                name="phoneNumber"
                size="medium"
                type="number"
                fullWidth
                placeholder="Ej. 1234567890"
                value={formik.values.phoneNumber}
                onChange={async (e) => {
                  const phoneNumber = e.target.value.replace(/\D/g, '').slice(0, 10);
                  formik.setFieldValue('phoneNumber', phoneNumber);
                }}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <AttachMoney fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Salario</FormLabel>
              </Stack>
              <TextField
                id="salary"
                name="salary"
                size="medium"
                fullWidth
                placeholder="Ingresar salario (mensual)"
                value={formik.values.salary}
                onChange={formik.handleChange}
                error={formik.touched.salary && Boolean(formik.errors.salary)}
                helperText={formik.touched.salary && formik.errors.salary}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Home fontSize="small" color="primary" />
                <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Dirección Física</FormLabel>
              </Stack>
              <TextField
                id="address"
                name="address"
                size="medium"
                fullWidth
                placeholder="Ingresar Dirección Completa"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ mt: 2, width: '100%', bgcolor: '#f8fafd', p: 3, borderRadius: 3, border: '1px solid #e3f2fd' }}>
                <FormLabel component="legend" sx={{ fontWeight: 700, color: 'primary.main', mb: 2, fontSize: '1.1rem' }}>Permisos del Sistema</FormLabel>
                <Grid container spacing={2}>
                  {menuItems.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.permissions.includes(item)}
                            onChange={(e) => {
                              const value = e.target.name;
                              const updatedPermissions = formik.values.permissions.includes(value)
                                ? formik.values.permissions.filter((p) => p !== value)
                                : [...formik.values.permissions, value];
                              formik.setFieldValue('permissions', updatedPermissions);
                            }}
                            name={item}
                            color="primary"
                          />
                        }
                        label={<Typography variant="body2">{item}</Typography>}
                        sx={{ m: 0, width: '100%', '&:hover': { bgcolor: 'rgba(21,101,192,0.05)' }, borderRadius: 1 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormControl>
            </Grid>

          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ borderRadius: 2, textTransform: 'none', px: 4, fontWeight: 600 }}
            >
              Guardar Cambios
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const menuItems = [
  'Tablero Principal',
  'Calendario de Reservas',
  'Pre-Reservas',
  'Conversaciones WhatsApp',
  'Reservas Confirmadas',
  'Centro de Tareas',
  'Control de Limpieza',
  'Gestión de Lavandería',
  'Operaciones de Caja',
  'Apertura de Caja',
  'Cierre de Caja',
  'Registro de Gastos',
  'Control de Inventario',
  'Directorio Proveedores',
  'Centros de Costo',
  'Categorías de Gastos',
  'Gestión de Facturas',
  'Pagos de Suscripción',
  'Gestión de Divisas',
  'Códigos Promocionales',
  'Centro de Restaurante',
  'Órdenes de Cocina (KOT)',
  'Reportes de Cocina',
  'Base de Clientes',
  'Administración de Personal',
  'Historial de Cajas',
  // Habilita a cuadrar el efectivo de una caja ya cerrada. Se verifica en el
  // servidor (controllers/cashRegister/scope.js), no solo en el menú.
  'Arqueo de Caja',
  // Habilita a ver el efectivo que los gerentes tienen por depositar y a
  // registrar depósitos. Se verifica en el servidor
  // (controllers/cashCustody/scope.js), no solo en el menú.
  'Custodia de Efectivo',
  // Habilita a ver y administrar el plan de cuentas. Se verifica en el servidor
  // (controllers/accounting/scope.js). Activar el módulo queda solo para el
  // administrador del hotel.
  'Plan de Cuentas',
  // Habilita a ver los reportes contables (mayor, comprobación, balance,
  // resultados). Solo lectura; se verifica en controllers/accounting/scope.js.
  'Reportes Contables',
  // Habilita a registrar y anular vouchers (asientos manuales). Escribe en el
  // libro; se verifica en controllers/accounting/scope.js.
  'Vouchers Contables',
  'Reporte de Ventas Diarias',
  'Análisis Financiero General',
  'Registro de Huéspedes',
  'Reporte de Restaurante',
  'Planes de Suscripción',
  'Configuración WhatsApp',
  'Configuración de Habitaciones',
  'Gestión de Habitaciones',
  'Tipos de Habitación',
  'Tipos de Estancia',
  'Tarifas y Precios',
  'Configuración del Hotel',
  'Reportar Problemas',
  // Módulo de Mantenimiento
  'Mantenimiento',
  'Órdenes de Trabajo',
  'Reportes y KPIs',
  'Preventivo',
  'Activos',
  'Plantillas',
  'Áreas Comunes'
];

EditEmployee.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  employeeData: PropTypes.object.isRequired
};

export default EditEmployee;
